import { useMemo, useState } from 'react';
import { FileText, Search, Trash2, Link2, MessageSquareQuote } from '@/components/icons';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { StatusBadge } from '@/components/ui/Badge';
import { SlaBadge } from '@/components/ui/SlaBadge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { deleteRfq, listRfqs, sendRfq, type Rfq } from '@/api/rfq';
import { apiError } from '@/lib/apiError';
import { formatDate } from '@/lib/format';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { RfqFormModal } from './RfqFormModal';
import { QuoteEntryModal } from './QuoteEntryModal';
import { PortalLinkModal } from './PortalLinkModal';

export function RfqPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const docViewer = useDocViewer();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Rfq | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteTarget, setQuoteTarget] = useState<Rfq | null>(null);
  const [portalOpen, setPortalOpen] = useState(false);
  const [portalTarget, setPortalTarget] = useState<Rfq | null>(null);

  const filter = useMemo(
    () => ({
      status: status || undefined,
      search: search || undefined,
      page,
      pageSize,
    }),
    [status, search, page, pageSize],
  );

  const query = useQuery({
    queryKey: ['rfq', filter],
    queryFn: () => listRfqs(filter),
    placeholderData: keepPreviousData,
  });

  const sendMutation = useMutation({
    mutationFn: sendRfq,
    onSuccess: (rfq) => {
      qc.invalidateQueries({ queryKey: ['rfq'] });
      toast.pushStep(`RFQ ${rfq.rfqNo} sent`, {
        body: 'Waiting on vendor quotes. AI scores each response as it comes in.',
        href: '/purchase/ai',
        nextLabel: 'Open AI Analysis',
        tag: `rfq-sent-${rfq.id}`,
      });
    },
    onError: (e: unknown) =>
      toast.push(apiError(e, 'Send failed'), 'error'),
  });

  const delMutation = useMutation({
    mutationFn: deleteRfq,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfq'] });
      toast.push('RFQ deleted', 'info');
    },
    onError: (e: unknown) =>
      toast.push(apiError(e, 'Delete failed'), 'error'),
  });

  async function onDelete(r: Rfq) {
    if (!await confirmDlg(`Delete ${r.rfqNo}? This cannot be undone.`)) return;
    delMutation.mutate(r.id);
  }

  const data = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div>
      <SectionHeader
        title="Quotations / RFQ"      />

      <FilterBar>
        <FilterField label="Search">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[240px]"
              aria-label="Search RFQs"
            />
          </div>
        </FilterField>
        <FilterField label="Status">
          <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option>Draft</option>
            <option>Sent</option>
            <option>Quotes Received</option>
            <option>Closed</option>
            <option>Cancelled</option>
          </Select>
        </FilterField>
        {/* Create chip lives inside the filter block on both viewports. */}
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
        >
          ＋ New RFQ
        </button>
      </FilterBar>

      <Table<Rfq>
        storageKey="rfq"
        title="RFQ Records"
        count={`Showing ${data.length} of ${total}`}
        rows={data}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        columns={[
          { key: 'rfqNo', header: 'RFQ #', width: '130px', cell: (r) => <span className="font-extrabold text-accent">{r.rfqNo}</span> },
          { key: 'date', header: 'Date', width: '110px', cell: (r) => formatDate(r.rfqDate) },
          { key: 'pr', header: 'PR Ref', width: '120px', cell: (r) => r.pr?.prNo ?? '—' },
          { key: 'vendors', header: 'Vendors', width: '80px', cell: (r) => `${r.vendorCount ?? r.vendors.length}` },
          { key: 'quotes', header: 'Quotes', width: '70px', cell: (r) => `${r.quotesReceived ?? 0}/${r.vendorCount ?? r.vendors.length}` },
          { key: 'deadline', header: 'Deadline', width: '110px', cell: (r) => formatDate(r.deadline ?? '') },
          { key: 'status', header: 'Status', width: '100px', cell: (r) => <StatusBadge status={r.status} /> },
          { key: 'sla', header: 'SLA', width: '60px', cell: (r) => <SlaBadge sla={r.sla ?? null} /> },
          {
            key: 'actions',
            header: '',
            width: '220px',
            cell: (r) => (
              <div className="flex items-center gap-1">
                <Button variant="glass" size="sm" onClick={() => { setEditing(r); setFormOpen(true); }}>View</Button>
                <Button variant="glass" size="sm" onClick={() => docViewer.open('rfq', r.id, r.rfqNo)} title="Open A4 document"><FileText size={14} /></Button>
                {r.status === 'Draft' && (
                  <>
                    <Button variant="primary" size="sm" onClick={() => sendMutation.mutate(r.id)} disabled={sendMutation.isPending}>Send</Button>
                    <Button variant="glass" size="sm" onClick={() => onDelete(r)} title="Delete"><Trash2 size={14} /></Button>
                  </>
                )}
                {(r.status === 'Sent' || r.status === 'Quotes Received') && (
                  <>
                    <Button variant="glass" size="sm" onClick={() => { setPortalTarget(r); setPortalOpen(true); }}><Link2 size={12} className="mr-1 inline-block align-[-2px]" /> Portal</Button>
                    <Button variant="glass" size="sm" onClick={() => { setQuoteTarget(r); setQuoteOpen(true); }}><MessageSquareQuote size={12} className="mr-1 inline-block align-[-2px]" /> Quote</Button>
                  </>
                )}
              </div>
            ),
          },
        ]}
        footer={<Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />}
      />

      <RfqFormModal open={formOpen} editing={editing} onClose={() => setFormOpen(false)} />
      <QuoteEntryModal open={quoteOpen} rfq={quoteTarget} onClose={() => setQuoteOpen(false)} />
      <PortalLinkModal open={portalOpen} rfq={portalTarget} onClose={() => setPortalOpen(false)} />
    </div>
  );
}
